/**
 * Generator maths shared by validation and the disruption meter.
 *
 * WHY THIS FILE EXISTS
 *
 * There are two different limits that are easy to conflate, and conflating them cost
 * a 10x-too-conservative amplitude cap:
 *
 *   1. **Per-report delta.** A USB mouse report carries `dx`/`dy` as `int8`, so no
 *      single report can move more than 127 px on an axis. This is a hard HID
 *      constraint and it applies to each STEP.
 *
 *   2. **Amplitude.** The radius of the box the cursor moves in. That is a POSITION
 *      bound, not a delta. Nothing about it is 127.
 *
 * The real amplitude ceiling comes from (1) applied to the generator's per-step
 * motion, which is a fraction of the amplitude:
 *
 *   CIRCLE         delta = amplitude * 2*sin(pi/64)  ~= amplitude * 0.0981
 *   ZIGZAG, DRIFT  delta = amplitude / 16
 *   RANDWALK       delta = jitter (the amplitude only bounds the position)
 *
 * Measured against the real firmware (steady state, first-step catch-up excluded),
 * CIRCLE reaches 123 at amplitude 1200 and saturates at 1300; ZIGZAG reaches 125 at
 * 2000. Both match the formulae above, which cross 127 at 1294 and 2032.
 *
 * Once the delta saturates, `Pattern_Emit()` clamps it and the shape stops being the
 * shape you asked for -- silently. That is the failure this function exists to catch.
 */

import { PatternKind } from '../protocol/constants.ts';
import type { Draft } from './draft.ts';
import type { JigStep } from '../protocol/types.ts';

/** One full generator cycle is 64 steps (GEN_STEPS_PER_PERIOD in Pattern.c). */
export const GENERATOR_STEPS_PER_PERIOD = 64;

/** What an int8 HID report can carry. */
export const MAX_DELTA = 127;

/**
 * Largest per-step delta this pattern will ask the device to emit.
 *
 * Mathematical, not a simulation: mirroring the generator algorithms here would
 * duplicate firmware logic and drift from it silently. The formulae are exact for
 * CIRCLE and for the linear sweep, and they OVERESTIMATE for RANDWALK, which is the
 * safe direction for a warning.
 */
export function maxStepDelta(kind: number, amplitude: number, jitter: number, steps: readonly JigStep[] = []): number {
  if (kind === PatternKind.MACRO) {
    return steps.reduce((m, s) => Math.max(m, Math.abs(s.dx), Math.abs(s.dy)), 0);
  }

  const amp = Math.max(0, amplitude);

  switch (kind) {
    case PatternKind.CIRCLE:
      return Math.ceil(amp * 2 * Math.sin(Math.PI / GENERATOR_STEPS_PER_PERIOD));

    case PatternKind.ZIGZAG:
    case PatternKind.DRIFT:
      // gen_step_px = 2*amp / (64/2), plus jitter added to the target.
      return Math.ceil(amp / 16) + Math.max(0, jitter);

    case PatternKind.RANDWALK:
      return Math.max(1, jitter);

    default:
      return 0;
  }
}

/**
 * The largest amplitude whose per-step delta still fits an int8 report, for a kind.
 * Exposed so the UI can say *why* a value is too big rather than just rejecting it.
 */
export function maxSafeAmplitude(kind: number): number {
  switch (kind) {
    case PatternKind.CIRCLE:
      return Math.floor(MAX_DELTA / (2 * Math.sin(Math.PI / GENERATOR_STEPS_PER_PERIOD))); // 1294
    case PatternKind.ZIGZAG:
    case PatternKind.DRIFT:
      return MAX_DELTA * 16; // 2032
    default:
      return Infinity; // RANDWALK's delta is bounded by jitter, not amplitude
  }
}

/** Convenience for a draft. */
export function draftMaxStepDelta(draft: Draft): number {
  return maxStepDelta(draft.kind, draft.amplitude, draft.jitter, draft.steps);
}
